'use client'

import { Button, ButtonProps } from '@/components/atoms/button/Button'
import { MediaImage } from '@/components/atoms/media'
import { MediaImageData } from '@/components/atoms/media/MediaImage'
import {
  ContactIconProps,
  ContactLinks,
} from '@/components/molecules/contactLinks'
import { ButtonVariants } from '@/types/enums'
import { buildImageUrl } from '@/utils/imageSharpUrlBuilder'
import {
  Box,
  chakra,
  Grid,
  GridItem,
  Heading,
  List,
  ListItem,
} from '@chakra-ui/react'
import { ButtonTypes } from '@/baseComponents'

export interface FooterColumnsProps {
  category: string
  links: ButtonProps[]
}

export interface FooterProps {
  footerColumns: FooterColumnsProps[]
  logo?: MediaImageData
  socialMediaIcons: ContactIconProps[]
}

export default chakra(Footer)
function Footer({ footerColumns, logo, socialMediaIcons }: FooterProps) {
  return (
    <chakra.footer
      backgroundColor='brand.grey'
      px={{
        base: 4,
        md: 10,
      }}
      pt={{
        base: 12,
        md: '4.5rem',
      }}
      pb={{
        base: 12,
        md: '6.4rem',
      }}
    >
      <Grid templateColumns='repeat(12,1fr)'>
        <GridItem
          colSpan={{
            base: 12,
            md: 3,
          }}
          pb={{
            base: 8,
            md: 0,
          }}
        >
          {logo && (
            <MediaImage
              {...logo}
              viewportWidthFactor={{ base: 0.2, lg: 0.15 }}
              cropSizeAlias={{ base: 'logo' }}
              hasBorderRadius={false}
              imageUrlBuilder={buildImageUrl}
              height='44px'
              width='130px'
            />
          )}
          <Box
            display='flex'
            gap={4}
            pt={{ base: 4, md: 8 }}
            mt={{ base: 4, md: 8 }}
            width='fit-content'
            position='relative'
            _before={{
              content: '""',
              position: 'absolute',
              top: 0,
              display: 'block',
              height: '0.0625rem',
              width: 'full',
              backgroundColor: 'aubergine.950',
              opacity: 0.2,
            }}
          >
            {socialMediaIcons && (
              <ContactLinks
                items={socialMediaIcons}
                buttonProps={{
                  buttonType: ButtonTypes.IconOnly,
                  buttonVariant: ButtonVariants.Secondary,
                }}
              />
            )}
          </Box>
        </GridItem>
        {footerColumns?.map((column, index) => {
          return (
            <GridItem
              key={index}
              colSpan={{
                base: 12,
                md: 3,
              }}
              pb={{
                base: 8,
                md: 0,
              }}
            >
              {column.category && (
                <Heading
                  color='brand.aubergine'
                  fontWeight={400}
                  lineHeight='100%'
                  fontSize='1.625rem'
                  as='h3'
                  paddingBottom={{
                    base: 4,
                    md: 8,
                  }}
                >
                  {column.category}
                </Heading>
              )}
              <List gap={2} display='flex' flexDir='column'>
                {column?.links?.map((link, index) => {
                  return (
                    <ListItem key={index}>
                      <Button
                        text={link.text}
                        url={link.url}
                        buttonType={ButtonTypes.TextOnly}
                        buttonVariant={ButtonVariants.TextNoPadding}
                        buttonProps={{ fontSize: 'md' }}
                      />
                    </ListItem>
                  )
                })}
              </List>
            </GridItem>
          )
        })}
      </Grid>
    </chakra.footer>
  )
}
